import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import { Player } from '@lordicon/react';
import ScrollReveal from 'scrollreveal';
import styles from '../../components/Services/Services.module.css';
import social from "../../icons/wired-outline-962-social-media-marketing-hover-pinch.json";
import camera from "../../icons/wired-outline-61-camera-hover-flash.json";
import adds from "../../icons/wired-outline-152-bar-chart-arrow-hover-growth.json";
import Button from '../../components/button/Button';

const ServicesShowcase = () => {
  const [selectedService, setSelectedService] = useState(null);

  const playerRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
  };

  const services = [
    {
      id: 1,
      icon: social,
      title: "תהליך של ניהול סושיאל מלא",
      description: "ניהול מלא של הנוכחות הדיגיטלית שלך",
      bgClass: styles.bgImage1,
      process: [
        "נבצע איפיון מלא לעסק ולמוצרים שלך ונבנה משפכים שיווקיים לפרסומים",
        "נחקור את קהל היעד שלך ונקודות הכאב שלו מכל הכיוונים",
        "נכין את התשתית הטכנית ונפתח קבוצת ווטסאפ ודרייב לעבודה משותפת",
        "נקיים פגישת קריאייטיב לאישור תסריטים מדויקים שתואמים את היעדים שלך",
        "ניפגש ליום צילום של 3 שעות פעם בחודש עם המון אנרגיה",
        "אערוך עבורך את כל החומרים לסרטונים ממירים לכל הרשתות",
        "תקבלי גאנט פרסומים חודשי מסודר לאישור ולסבב תיקונים",
        "כל התכנים יעלו לעמוד ואדאג שהעמוד יתקתק ויראה מעולה",
        "אני אלווה אותך בניהול ופרסום הסטורי ברשתות שלך לשמירה על אותנטיות",
        "נעקוב אחרי הנתונים ונוציא דוח חודשי כדי למקסם תוצאות",
        "נתכנן מראש את הלו\"ז להמשך התהליך כדי לשמור על רצף וצמיחה"
      ]
    },
    {
      id: 2,
      icon: camera,
      title: "תהליך שיווקי של יצירת תוכן",
      description: "יום צילום מקצועי עם כל החומרים",
      bgClass: styles.bgImage2,
      process: [
        "נקיים פגישת חקר מעמיקה לדיוק המסרים השיווקיים שהכי נכונים לעסק שלך",
        "נצא ליום צילומים אצלך בעסק במשך 3 שעות של יצירת תוכן ויזואלי מדויק",
        "אערוך עבורך את כל החומרים לסרטונים שמעלים את הרמה של העמודים שלך",
        "תקבלי את כל התוצרים בתיקייה מסודרת, כולל סבב תיקונים עד לשביעות רצונך",
        "תקבלי סרטון הדרכה מפורט עם כל מה שאת צריכה לדעת כדי להמשיך מכאן לבד",
        "הכל מוכן אצלך בידיים. זה הזמן להעלות את התוכן ולהתחיל למשוך את לקוחות המדוייקים עבורך"
      ]
    },
    {
      id: 3,
      icon: adds,
      title: "ייעוץ וליווי שיווקי פרטני",
      description: "מנטורית שיווקית אישית לעסק שלך",
      bgClass: styles.bgImage6,
      process: [
        "נאפיין את קהל היעד שלך ונבנה משפך שיווקי ותוכנית עבודה ממוקדת",
        "נפתח קבוצת ווטסאפ ייעודית ודרייב משותף לניהול שוטף של כל חומרי השיווק",
        "נקיים פגישות עדכון שבועיות לבניית לו\"ז תוכן ודיוק האסטרטגיה לקהילה שלך",
        "נצא ליום צילומים של 3 שעות אצלך בעסק כדי לייצר חומרי גלם איכותיים ומדויקים",
        "אערוך עבורך את כל הסרטונים שצילמנו לרמה הגבוהה והממגנטת ביותר",
        "נשלב את התכנים בפרסומים האורגניים ובממומן כדי להרחיב את החשיפה והקהל",
        "נקיים פגישת סיכום ותכנון קדימה כדי להבטיח עקביות וצמיחה מתמשכת",
        "אהיה זמינה עבורך לליווי, תמיכה ומענה בווטסאפ לכל שאלה שצצה בדרך"
      ]
    }
  ];

  const handleComplete = (id) => {
    setTimeout(() => {
      playerRefs[id]?.current?.playFromBeginning();
    }, 2500);
  };

  useEffect(() => {
    Object.values(playerRefs).forEach(ref => {
      ref?.current?.playFromBeginning();
    });
  }, []);

  useEffect(() => {
    ScrollReveal().reveal(`.${styles.icon}`, {
      duration: 1000,
      distance: "100px",
      origin: "left",
      easing: "ease-out",
      reset: false,
      viewFactor: 0.2,
      interval: 300,
      delay: 200,
      scale: 1,
    });
  }, []);

  const handleWhatsAppClick = (serviceTitle) => {
    const phoneNumber = "972508727802";
    const message = `היי נטלי, הגעתי מהדף שלך, אשמח לשמוע עוד על ${serviceTitle}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const ServiceOverlay = ({ service, onClose }) => {
    const overlayPlayerRef = useRef(null);

    useEffect(() => {
      if (service) {
        overlayPlayerRef?.current?.playFromBeginning();
      }
    }, [service]);

    const handleOverlayComplete = () => {
      setTimeout(() => {
        overlayPlayerRef?.current?.playFromBeginning();
      }, 2500);
    };

    if (!service) return null;

    return (
      <div className={styles.overlayBackdrop} onClick={onClose}>
        <div className={styles.overlayContent} onClick={(e) => e.stopPropagation()}>
          <button className={styles.overlayClose} onClick={onClose} aria-label="סגור">
            <X size={24} />
          </button>

          <div className={styles.overlayHeader}>
            <div className={styles.overlayIcon}>
              <Player
                icon={service.icon}
                ref={overlayPlayerRef}
                size="100%"
                onComplete={handleOverlayComplete}
              />
            </div>
            <h3 className={styles.overlayTitle}>{service.title}</h3>
            <p className={styles.overlayDescription}>{service.description}</p>
          </div>

          <div className={styles.overlayProcess}>
            <h4 className={styles.processTitle}>התהליך שלנו:</h4>
            <div className={styles.processSteps}>
              {service.process.map((step, index) => (
                <div key={index} className={styles.processStep}>
                  <div className={styles.stepNumber}>{index + 1}</div>
                  <div className={styles.stepContent}>
                    <ChevronLeft className={styles.stepArrow} size={16} />
                    <p className={styles.stepText}>{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className={styles.overlayCta}
            onClick={() => handleWhatsAppClick(service.title)}
          >
            בואו נתחיל לעבוד ביחד
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>
              <div className={styles.titleHighlight}>התהליכים השיווקיים איתי</div>
            </div>
            <p className={styles.subtitle}>
              לחצו על כל תהליך כדי לקבל עוד פרטים
            </p>
          </div>

          <div className={styles.grid}>
            {services.map((service) => (
              <div
                key={service.id}
                className={`${styles.card} ${service.bgClass}`}
                onClick={() => setSelectedService(service)}
              >
                <div className={styles.icon}>
                  <Player
                    icon={service.icon}
                    ref={playerRefs[service.id]}
                    size="100%"
                    onComplete={() => handleComplete(service.id)}
                  />
                </div>
                <h3 className={styles.cardTitle}>{service.title}</h3>
                <p className={styles.description}>{service.description}</p>
                <button
                  className={styles.button}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedService(service);
                  }}
                >
                  על התהליך
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedService && (
        <ServiceOverlay
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}

      <Button text="נטלי, אני רוצה לדעת מה מתאים לי יחד איתך!" />
    </>
  );
};

export default ServicesShowcase;
